import React, {
    useState, useCallback, useEffect, useMemo, FC,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import Preloader from 'components/Preloader';
import EmptyContent from 'components/EmptyContent';
import { getClientId } from 'utils';
import { useCancelAllRequests } from 'hooks/useCancelAllRequests';
import { useDetectedMobileDevice } from 'hooks/useDetectedMobileSize';
import SelectElement from 'components/AtomicStructure/atoms/SelectElement';
import { Table } from '../../../../ui-library';
import { getColumns } from './constants';
import { useDocuments } from './hooks/useDocuments';
import { DocumentUpload } from './components/DocumentUpload';
import Card from '../../../../components/Card/Card';
import { useFormatting } from '../../../../locale';
import { TableFilter, TableFilterItem } from '../../../../components/TableFilter';
import './Documents.css';

// --- Interfaces ---

interface LocationState {
    currentPage?: number;
    currentTab?: 'personal' | 'general';
}

interface ApiError {
    error?: {
        response?: {
            data?: {
                message?: string;
            };
        };
    };
    message?: string;
    [key: string]: unknown;
}

interface SelectOption {
    label: string;
    id: string;
}

const Documents: FC = () => {
    useCancelAllRequests();

    const { dfsClientId } = useParams<{ dfsClientId: string }>();
    const location = useLocation();
    const state = location.state as LocationState;

    const currentPage = state?.currentPage || 1;
    const tab = state?.currentTab || 'personal';

    const clientId = getClientId(dfsClientId);
    const { t } = useTranslation();
    const { isTabletSize } = useDetectedMobileDevice();
    const { getFormattedDate } = useFormatting();

    // State
    const [currentPagination, setCurrentPagination] = useState<number>(currentPage);
    const [docError, setDocError] = useState<ApiError | null>(null);
    const [businessUnitFilter, setBusinessUnitFilter] = useState<string | null>(null);
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

    const {
        data: fullData,
        isLoading,
        error: useDocumentsError,
        pageSize,
        personalPaging,
        categories,
        businessUnits,
        getUnifiedDocuments,
    } = useDocuments({
        clientId: clientId as string | number,
        currentPagination,
        tab,
        businessUnitFilter: businessUnitFilter ?? undefined,
        categoryFilter: categoryFilter ?? undefined,
    });

    // Memoized Filter Options
    const typeFilterOptions = useMemo<SelectOption[]>(() => [
        { label: t('clientDashboard.documents.allCategories'), id: '' },
        ...(categories?.filter(category => !!category).map(i => ({ label: i, id: i })) || []),
    ], [categories, t]);

    const productFilterOptions = useMemo<SelectOption[]>(() => [
        { label: t('clientDashboard.documents.allBusinessUnits'), id: '' },
        ...(businessUnits?.map(i => ({ label: i, id: i })) || []),
    ], [businessUnits, t]);

    // Sync external error with internal state and apply custom messaging
    useEffect(() => {
        if (useDocumentsError) {
            const castedError = useDocumentsError as ApiError;
            const errMsg = castedError?.error?.response?.data?.message?.toLowerCase();

            if (errMsg?.includes('not found') || errMsg?.includes('not exist')) {
                setDocError({
                    ...castedError,
                    message: t('clientDashboard.documents.noDocFound'),
                });
            } else {
                setDocError(castedError);
            }
        } else {
            setDocError(null);
        }
    }, [useDocumentsError, t]);

    const onChangePage = useCallback((paging: { currentPage: number }) => {
        setCurrentPagination(paging?.currentPage);
    }, []);

    const onUploadSuccess = useCallback(async (_id: unknown): Promise<void> => {
        await getUnifiedDocuments();
    }, [getUnifiedDocuments]);

    return (
        <div className="container mb-40">
            <Card
                title={t('advisoryDashboard.documents.title')}
                titleTagName="h1"
                headerActions={(
                    <DocumentUpload
                        contactId={Number(clientId)}
                        onUpload={onUploadSuccess}
                    />
                )}
            >
                <TableFilter>
                    <TableFilterItem>
                        <SelectElement
                            hasSearch
                            className="filters"
                            placeholder={t('clientDashboard.documents.category')}
                            options={typeFilterOptions}
                            value={categoryFilter || ''}
                            onChange={(val: string) => setCategoryFilter(val)}
                        />
                    </TableFilterItem>
                    <TableFilterItem>
                        <SelectElement
                            className="filters"
                            placeholder={t('clientDashboard.documents.businessUnit')}
                            options={productFilterOptions}
                            value={businessUnitFilter || ''}
                            onChange={(val: string) => setBusinessUnitFilter(val)}
                        />
                    </TableFilterItem>
                </TableFilter>

                <Preloader
                    isLoading={isLoading}
                    error={docError}
                    letChildrenToRenderOnError
                >
                    <EmptyContent
                        data={fullData}
                        text={t('advisoryDashboard.documents.noDocuments')}
                    >
                        <Table
                            columns={getColumns(t, isTabletSize, getFormattedDate)}
                            data={fullData}
                            paging={{
                                total: personalPaging?.TotalCount || 0,
                                pageSize,
                                currentPage: currentPagination,
                            }}
                            headerControls={{
                                selectControlsLabel: {
                                    selectAll: t('headerControls.selectAll'),
                                    reset: t('headerControls.reset'),
                                },
                            }}
                            onChange={onChangePage}
                        />
                    </EmptyContent>
                </Preloader>
            </Card>
        </div>
    );
};

export default Documents;
